/* eslint-disable react-hooks/exhaustive-deps */
import { CubeTransparentIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { NewspaperIcon, PresentationChartBarIcon } from "@heroicons/react/24/outline";
import { Type, Sparkles } from "lucide-react";
import React, { useMemo, useState } from "react";
import HubspotIcon from "../../../assets/images/hubspot.svg?react";
import He from "../../../assets/images/heSm.svg?react";
import Segment from "../../../assets/images/segmentAvatar.svg?react";
import { Input } from "../../../components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import cn from "../../../utils/styleHelpers";

type PopoverPosition =
  | "bottom"
  | "left"
  | "right"
  | "top"
  | "bottom-end"
  | "bottom-start"
  | "left-end"
  | "left-start"
  | "right-end"
  | "right-start"
  | "top-end"
  | "top-start";

type Props = {
  list?: any[];
  selected: any;
  setSelected: any;
  placeholder?: string;
  type?: string;
  successMetricsList?: any[];
  className?: string;
  position?: PopoverPosition;
};

type SourceFilter = {
  name: string;
  label: string;
  icon: React.ElementType;
};

type AttributeFilter = {
  name: "all" | "traits" | "success_metrics";
  label: string;
  icon: React.ElementType;
};

const listOfSources: SourceFilter[] = [
  {
    name: "all",
    label: "All Sources",
    icon: CubeTransparentIcon,
  },
  {
    name: "native",
    label: "Hyperengage",
    icon: He,
  },
  {
    name: "segment",
    label: "Segment",
    icon: Segment,
  },
  {
    name: "hubspot",
    label: "HubSpot",
    icon: HubspotIcon,
  },
];

const listOfAttributes: AttributeFilter[] = [
  {
    name: "all",
    label: "All",
    icon: CubeTransparentIcon,
  },
  {
    name: "traits",
    label: "Traits",
    icon: NewspaperIcon,
  },
  {
    name: "success_metrics",
    label: "Success Metrics",
    icon: Sparkles,
  },
];

const getPopoverPlacement = (position: PopoverPosition) => {
  const [sideRaw, alignRaw] = position.split("-") as [string, string | undefined];
  const side = (["top", "bottom", "left", "right"].includes(sideRaw) ? sideRaw : "bottom") as "top" | "bottom" | "left" | "right";
  const align = (alignRaw === "start" || alignRaw === "end" ? alignRaw : "start") as "start" | "center" | "end";
  return { side, align };
};

const sourcePillClasses: Record<string, string> = {
  native: "bg-primary/10 text-primary border-primary/20",
  segment: "bg-blue-50 text-blue-700 border-blue-200",
  hubspot: "bg-orange-50 text-orange-700 border-orange-200",
};

const SingleSelectDropdown = ({
  list = [],
  successMetricsList = [],
  type = "Fields",
  selected,
  setSelected,
  position = "left-start",
  className = "",
  placeholder = "Select Property",
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [selectedSource, setSelectedSource] = useState<string>("all");
  const [selectedAttribute, setSelectedAttribute] = useState<"all" | "traits" | "success_metrics">("all");

  const displayTraits = useMemo(
    () => (
      selectedSource === "all"
        ? list
        : list.filter((item: any) => item?.source === selectedSource)
    ),
    [selectedSource, list],
  );

  const displaySuccessMetrics = useMemo(
    () => (
      selectedSource === "all"
        ? successMetricsList
        : successMetricsList.filter((item: any) => item?.source === selectedSource)
    ),
    [selectedSource, successMetricsList],
  );

  const queriedData = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const buckets = {
      traits: displayTraits,
      success_metrics: displaySuccessMetrics,
      all: [...displayTraits, ...displaySuccessMetrics],
    };

    const pool = buckets[selectedAttribute] || [];
    return normalizedQuery
      ? pool.filter((item: any) => item?.display_name?.toLowerCase().includes(normalizedQuery))
      : pool;
  }, [query, selectedAttribute, displayTraits, displaySuccessMetrics]);

  const { side, align } = getPopoverPlacement(position);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted",
            selected?.display_name && "text-foreground",
            !selected?.display_name && "text-muted-foreground",
            className,
          )}
        >
          {selected?.display_name || placeholder}
          <svg className="h-3 w-3 text-muted-foreground" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align={align} sideOffset={4} collisionPadding={20} avoidCollisions className="w-[360px] max-h-[min(420px,var(--radix-popover-content-available-height))] p-0 z-[1000]">
        <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-card max-h-[inherit]">
          <div className="border-b border-border p-3">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.currentTarget.value)}
                placeholder={`Search ${type}`}
                onKeyDown={(event) => event.stopPropagation()}
                className="h-8 pl-8"
              />
            </div>
          </div>

          <div className="space-y-3 border-b border-border bg-muted/40 p-3">
            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Source</div>
              <div className="flex flex-wrap gap-1.5">
                {listOfSources.map((source) => {
                  const SourceIcon = source.icon;
                  const active = selectedSource === source.name;
                  return (
                    <button
                      key={source.name}
                      type="button"
                      onClick={() => {
                        setSelectedSource(source.name);
                        if (selectedAttribute !== "all") setSelectedAttribute("all");
                      }}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors",
                        active
                          ? sourcePillClasses[source.name] || "bg-primary/10 text-primary border-primary/20"
                          : "border-border bg-card text-muted-foreground hover:bg-muted",
                      )}
                    >
                      <SourceIcon className="h-3.5 w-3.5" />
                      <span>{source.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Type</div>
              <div className="inline-flex flex-wrap rounded-md border border-border bg-card p-0.5 gap-0.5">
                {listOfAttributes
                  .filter((item) => {
                    if (item.name === "success_metrics") return successMetricsList.length > 0;
                    return true;
                  })
                  .map((item) => {
                  const Icon = item.icon;
                  const active = selectedAttribute === item.name;
                  return (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => setSelectedAttribute(item.name)}
                      className={cn(
                        "inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors whitespace-nowrap",
                        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted",
                      )}
                    >
                      <Icon className="h-3 w-3 flex-shrink-0" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs text-muted-foreground">
            <span>{queriedData.length} results</span>
          </div>

          <div className="max-h-[300px] overflow-y-auto">
            {queriedData.length ? queriedData.map((item: any) => {
              const isSelected = selected?.name === item?.name;
              const isComputed = item?.generation_type === "computed_trait" || item?.attribute_type === "computed_traits";
              const isMetric = !isComputed && (item?.attribute_type === "metric_traits" || item?.attribute_type === "metrics");

              return (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => {
                    setSelected(item);
                    setIsOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left transition-colors last:border-0",
                    isSelected ? "bg-primary/5" : "hover:bg-muted/70",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className={cn(
                      "truncate text-sm",
                      isSelected ? "font-medium text-foreground" : "text-foreground",
                    )}>
                      {item.display_name}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <span className={cn(
                        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium",
                        isComputed
                          ? "bg-violet-50 text-violet-700"
                          : isMetric
                          ? "bg-amber-50 text-amber-700"
                          : "bg-emerald-50 text-emerald-700",
                      )}
                      >
                        {isComputed ? <Sparkles className="h-3 w-3" /> : isMetric ? <PresentationChartBarIcon className="h-3 w-3" /> : <Type className="h-3 w-3" />}
                        {isComputed ? "Computed" : isMetric ? "Metric" : "Trait"}
                      </span>
                      {item?.source && item.source !== "all" && (
                        <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          {item.source}
                        </span>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <svg className="h-4 w-4 flex-shrink-0 text-primary" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              );
            }) : (
              <div className="flex h-[180px] flex-col items-center justify-center gap-2 px-4 text-center">
                <CubeTransparentIcon className="h-5 w-5 text-muted-foreground/70" />
                <p className="text-sm font-medium text-muted-foreground">No matching items found</p>
                <p className="text-xs text-muted-foreground/80">Try a different search or filter.</p>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SingleSelectDropdown;
